const compact = (values = []) => values.filter((value) => value !== undefined && value !== null && value !== '');

const assetUrl = (asset, fallback = '') => {
  if (!asset) return fallback || '';
  if (typeof asset === 'string') return asset;
  return asset.displayLink || asset.storedLink || fallback || '';
};

const readStoredLogoUrl = () => {
  if (typeof window === 'undefined') return '';
  try {
    return localStorage.getItem('companyLogo') || '';
  } catch (error) {
    return '';
  }
};

const getDynamicAttributeValue = (company = {}, names = []) => {
  const attributes = Array.isArray(company?.dynamicAttributes) ? company.dynamicAttributes : [];
  const normalizedNames = names.map((name) => String(name || '').toLowerCase());
  const match = attributes.find((attribute) => normalizedNames.includes(String(attribute?.name || '').toLowerCase()));
  return match?.value || '';
};

const safeParse = (value) => {
  if (!value || typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const getStoredActiveTemplate = () => {
  if (typeof window === 'undefined') return null;
  return safeParse(localStorage.getItem('activeTemplate')) || safeParse(sessionStorage.getItem('activeTemplate'));
};

const hasExplicitEmptyWaasBrandName = () => {
  const template = getStoredActiveTemplate();
  const header = template?.attributes?.header || {};
  if (!Object.prototype.hasOwnProperty.call(header, 'brandName')) return false;
  return String(header.brandName || '').trim() === '';
};

const clearDefaultWaasBrandFallback = () => {
  if (typeof document === 'undefined' || !hasExplicitEmptyWaasBrandName()) return;

  document.querySelectorAll('#waas-template-header .waas-template-header-inner span').forEach((node) => {
    if (node.childElementCount === 0 && node.textContent?.trim() === 'Your IT Company') {
      node.textContent = '';
    }
  });
};

const installWaasEmptyBrandNameGuard = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__avpsWaasEmptyBrandNameGuardInstalled) return;
  window.__avpsWaasEmptyBrandNameGuardInstalled = true;

  let frameId = 0;
  const schedule = () => {
    if (frameId) return;
    frameId = window.requestAnimationFrame(() => {
      frameId = 0;
      clearDefaultWaasBrandFallback();
    });
  };

  window.addEventListener('storage', schedule);
  window.addEventListener('avps-template-updated', schedule);
  const observer = new MutationObserver(schedule);
  observer.observe(document.documentElement, { childList: true, subtree: true, characterData: true });
  schedule();
};

installWaasEmptyBrandNameGuard();

export const selectTemplateCompanyState = (state) => (
  state?.companyReducer?.company
  || state?.company?.companyDetails
  || {}
);

export const getTemplateCompanyContext = (...sources) => (
  sources.filter((source) => source && typeof source === 'object').reduce((current, source) => {
    const companyDetails = source?.companyDetails && typeof source.companyDetails === 'object' ? source.companyDetails : {};
    const logoImageContent = {
      ...(current.logoImageContent || {}),
      ...(companyDetails.logoImageContent && typeof companyDetails.logoImageContent === 'object' ? companyDetails.logoImageContent : {}),
      ...(source.logoImageContent && typeof source.logoImageContent === 'object' ? source.logoImageContent : {})
    };

    return {
      ...current,
      ...companyDetails,
      ...source,
      companyDetails: {
        ...(current.companyDetails || {}),
        ...companyDetails
      },
      logoImageContent,
      legalDocuments: Array.isArray(source.legalDocuments)
        ? source.legalDocuments
        : Array.isArray(companyDetails.legalDocuments)
          ? companyDetails.legalDocuments
          : current.legalDocuments,
      socialMediaAccounts: Array.isArray(source.socialMediaAccounts)
        ? source.socialMediaAccounts
        : Array.isArray(companyDetails.socialMediaAccounts)
          ? companyDetails.socialMediaAccounts
          : current.socialMediaAccounts
    };
  }, {})
);

export const getCompanyLogoUrl = (company = {}) => (
  compact([
    assetUrl(company.logoImageContent),
    assetUrl(company.logoContent),
    company.logoLocation,
    company.logoUrl,
    company.logoURL,
    company.logo,
    assetUrl(company.companyDetails?.logoImageContent),
    company.companyDetails?.logoLocation,
    company.companyDetails?.logoUrl,
    getDynamicAttributeValue(company, ['companyLogo', 'company_logo', 'logoUrl', 'logoLocation']),
    readStoredLogoUrl()
  ])[0] || ''
);

export const formatPolicyLabel = (value = '') => {
  const label = String(value || '').trim();
  if (!label) return 'Legal Document';
  return label
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const getCompanyLegalDocuments = (company = {}) => {
  if (Array.isArray(company.legalDocuments)) return company.legalDocuments;
  if (Array.isArray(company.companyDetails?.legalDocuments)) return company.companyDetails.legalDocuments;
  return [];
};

export const normalizePolicyKey = (value = '') => (
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
);

export const findCompanyLegalDocument = (company = {}, policyKey = '') => {
  const normalizedPolicyKey = normalizePolicyKey(policyKey);
  if (!normalizedPolicyKey) return undefined;

  return getCompanyLegalDocuments(company).find((document) => (
    normalizePolicyKey(document?.contentSubType || document?.name) === normalizedPolicyKey
  ));
};

export const getCompanyPolicyLinks = (company = {}) => (
  getCompanyLegalDocuments(company)
    .map((document) => {
      const contentSubType = document?.contentSubType || document?.name || '';
      const policyName = normalizePolicyKey(contentSubType);
      if (!policyName) return null;

      return {
        label: formatPolicyLabel(document?.contentSubType || document?.name),
        route: `/policy/${policyName}`,
        visible: true,
        document
      };
    })
    .filter(Boolean)
);
